import React, { createContext, useContext } from "react";
import { useParams } from "react-router-dom";
import { useCurrentProjectRole } from "@/hooks/use-current-project-role";
import { ProjectMemberRole } from "@/types/project";

export interface ProjectRoleContextValue {
  role: ProjectMemberRole | undefined;
  isOwner: boolean;
  isAdmin: boolean;
  isViewer: boolean;
  // True when the current user's system role is VIEWER (globally read-only,
  // independent of project membership).
  isSystemViewer: boolean;
  canEdit: boolean;
  isLoading: boolean;
}

const ProjectRoleContext = createContext<ProjectRoleContextValue | undefined>(
  undefined,
);

export const ProjectRoleProvider: React.FC<{
  projectId?: string;
  children: React.ReactNode;
}> = ({ projectId, children }) => {
  const { id: paramId } = useParams();
  const value = useCurrentProjectRole(projectId ?? paramId);

  return (
    <ProjectRoleContext.Provider value={value}>
      {children}
    </ProjectRoleContext.Provider>
  );
};

export const useProjectRole = (): ProjectRoleContextValue => {
  const ctx = useContext(ProjectRoleContext);
  // Fallback: when used outside a provider (e.g. inside a global panel/drawer
  // rendered above the route tree), derive the role from URL params directly.
  // React Query caches the underlying members fetch, so this is cheap.
  const fallback = useCurrentProjectRole();
  return ctx ?? fallback;
};
